import { useEffect, useState, useRef, useCallback } from 'react';

import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import Review from '../components/signup/Review';
import { useAuth } from '../utils/AuthContext';
import { Application } from '../types/dbTypes';
import { useUser } from '../utils/UserContext';
import {
  ApplicationStep,
  PatchApplicationRequest,
  Entity,
  VendorSignupStep,
  ApplicationStatus,
  PartnerSignupStep,
} from '../types/apiTypes';
import SignupBreadcrumbs from '../components/signup/SignupBreadcrumbs';
import AccountInfo, {
  PersonalFormData,
} from '../components/signup/customers/AccountInfo';
import BusinessInfo, {
  BusinessInfoForm,
} from '../components/signup/customers/BusinessInfo';
import ChoosePlan from '../components/signup/customers/ChoosePlan';
import HeadsUp from '../components/signup/customers/HeadsUp';
import {
  ASSET_PREFILL_STORAGE_KEY,
  HEADS_UP_ENABLED,
  HEADS_UP_TOPBAR_STEP,
  PREFILL_SUGGESTIONS_STORAGE_KEY,
} from '../constants/headsUp';
import {
  CARRIER_NUMBER_ERROR_STORAGE_KEY,
  customerHasCarrierIdentity,
} from '../utils/carrierIdentity';
import { formatUSPhoneForDisplay } from '../utils/phone';
import { AxiosResponse, HttpStatusCode } from 'axios';
import useApi from '../utils/AxiosWrapper';
import PendingReview from '../components/signup/customers/PendingReview';
import { useScreenView } from '../utils/ScreenViewContext';
import VendorBusinessDetails from '../components/signup/vendors/VendorBusinessDetails';
import VendorPersonalInfo from '../components/signup/vendors/VendorPersonalInfo';
import VendorManagementContact from '../components/signup/vendors/VendorManagementContacts';
import BusinessAssets from '../components/signup/customers/BusinessAssets';
import Agreements from '../components/signup/customers/agreements/Agreements';
import PartnerBusinessDetails from '../components/signup/partners/PartnerBusinessDetails';
import PartnerPersonalInfo from '../components/signup/partners/PartnerPersonalInfo';
import PartnerManagementContact from '../components/signup/partners/PartnerManagementContacts';
import { useAuthRouter } from '../utils/AuthRouter';
import { formatDate } from '../utils/DateUtils';
import {
  AddressParts,
  getFullAddress,
  isCompleteAddress,
} from '../utils/AddressUtils';
import PartnerContract from '../components/signup/partners/PartnerContract';
import {
  safeGetItem,
  safeSessionGetItem,
  safeSessionRemoveItem,
  safeSessionSetItem,
  safeSetItem,
} from '../shared/safeStorage';

/**
 * Steps removed from the signup flow, and the live screen an application still parked on one is
 * shown instead. `renderSignupStep` has no case for any of them, so a missing entry renders
 * NOTHING and strands the customer on a blank page.
 *
 * Every remap is RENDER-TIME ONLY — the step is deliberately never written back. For
 * bank_agreement that is load-bearing: a transition into master_service_agreement re-runs the
 * Highway lookup and starts a duplicate underwriting run. The others follow the same rule for
 * consistency, and the sign / upsert endpoints accept the old step for exactly this reason.
 */
const DEPRECATED_STEP_REMAP: Record<string, string> = {
  // The step business_debt used to flow into.
  [ApplicationStep.BusinessDebt]: ApplicationStep.Review,
  // Re-runs the Assets submit, which now transitions straight to pending_review / pre-active.
  [ApplicationStep.PlaidIDV]: ApplicationStep.Assets,
  // The step the removed prequal screen always advanced to — on approval AND on rejection — so
  // nothing is skipped. upsertApplication accepts asset_summary on either step for this.
  [ApplicationStep.PrequalDecision]: ApplicationStep.Assets,
  // master_service_agreement now presents both agreements. Customers parked here have already
  // signed the MSA and will simply re-sign both.
  [ApplicationStep.BankAgreement]: ApplicationStep.MasterServiceAgreement,
};

function Signup() {
  const { userRecord, userId, refetchUser } = useUser();
  const [currStep, setCurrStep] = useState(
    userRecord?.userType === Entity.Customer
      ? 'business_info'
      : 'vendor_business_info'
  );
  const [selectedRole, setSelectedRole] = useState<Entity>(Entity.Customer);
  const { isAuthenticated } = useAuth();
  const { isLoading } = useAuth0();
  const { callProtectedApi } = useApi();
  const [loading, setLoading] = useState(true);
  const { screenView } = useScreenView();
  const [application, setApplication] = useState<Application>(
    {} as Application
  );
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get('code') || '';
  const tachref = searchParams.get('tachref') || '';
  useAuthRouter(); // Handle auth routing
  const applicationFetchInProgress = useRef(false);
  const [personalInfo, setPersonalInfo] = useState<PersonalFormData | null>(
    null
  );
  const [businessInfo, setBusinessInfo] = useState<BusinessInfoForm | null>(
    null
  );
  // Heads-up page (THT-729): shown once, between login and Business Info, to a pending customer
  // who has no MC/DOT on file yet. Frontend-only — deliberately NOT an ApplicationStep.
  const [headsUpCompleted, setHeadsUpCompleted] = useState(false);
  /**
   * The customer asked to change the MC/DOT we already hold, from the summary on Business Info
   * (THT-711).
   *
   * It reopens the page rather than editing the number in place because the number is not a
   * field on its own: it drives a carrier lookup they confirm by name, and then a prefill. A
   * corrected number has to clear the same bar the first one did, and this page is where that
   * bar lives.
   */
  const [editingCarrierIdentity, setEditingCarrierIdentity] = useState(false);
  /**
   * The Review transition bounced this customer back here to fix an identifier the backend
   * rejected (400 `invalidIdentifier`), and its toast told them to read the warning on Business
   * Info. That warning is on Business Info, so the heads-up page has to stand aside — otherwise
   * a rejected number, which now also means NO number at all (THT-752), would reopen this page
   * instead and the customer would be told to look at a message they were never shown.
   *
   * Latched rather than read once at mount, because the bounce happens BOTH ways: AccountInfo
   * sets the flag and calls `setStep` without this page remounting, so a mount-time read would
   * miss the very case it exists for — and latched rather than read live, because Business Info
   * clears the flag as it mounts (the effect that raises the warning banner), which would
   * otherwise pull the step out from under the customer the moment that banner appeared. The
   * latch beats that clear because this render body runs before any child effect does; anything
   * that moves the clear EARLIER than Business Info's mount has to keep that ordering.
   */
  const bouncedForInvalidIdentifier = useRef(false);
  if (safeSessionGetItem(CARRIER_NUMBER_ERROR_STORAGE_KEY) === 'true') {
    bouncedForInvalidIdentifier.current = true;
  }
  const customerIsMissingCarrierIdentity =
    userRecord?.userType === Entity.Customer &&
    userRecord?.status === 'pending' &&
    // Shared with the Business Info slim-down — an identifier on the record, and nothing else,
    // is what counts as collected (THT-752).
    !customerHasCarrierIdentity(userRecord);
  const showHeadsUp =
    // See HEADS_UP_ENABLED. With it false nothing below matters and the customer lands on
    // Business Info exactly as they did before this page existed.
    HEADS_UP_ENABLED &&
    // Only ever shown alongside Business Info, on both paths in. An applicant whose fetched
    // application sits on a later step must resume there — interposing this page would hand them
    // back to Business Info, whose submit rewinds application_step and loses their progress.
    currStep === ApplicationStep.BusinessInfo &&
    (editingCarrierIdentity ||
      (!headsUpCompleted &&
        !bouncedForInvalidIdentifier.current &&
        customerIsMissingCarrierIdentity));

  // Store tachref in localStorage if present in URL
  useEffect(() => {
    if (tachref) {
      safeSetItem('tachref', tachref);
    }
  }, [tachref]);

  useEffect(() => {
    if (
      userRecord?.userType === Entity.Partner ||
      userRecord?.userType === Entity.Vendor
    ) {
      setLoading(false);
    }
    // For active and pre-active customers, we don't need to load application
    if (
      userRecord?.status === 'active' ||
      userRecord?.status === 'pre_active'
    ) {
      setLoading(false);
    }
  }, [userRecord]);

  const populateApplicationData = useCallback(() => {
    const localUserRecord: any = userRecord || {};
    const businessAddressParts: AddressParts = {
      address_st_name: localUserRecord?.address_st_name || '',
      address_st_number: localUserRecord?.address_st_number || '',
      address_unit: localUserRecord?.address_unit || '',
      address_city: localUserRecord?.address_city || '',
      address_state: localUserRecord?.address_state || '',
      address_zip: localUserRecord?.address_zip || '',
    };
    /**
     * Only a COMPLETE address composes into the one-liner the address bar shows.
     *
     * That field is what Business Info treats as proof of a Google Places selection, and a bar
     * filled with an address Places never returned is worse than an empty one: it looks answered
     * and then fails submit with "you must select an address from the dropdown", with nothing on
     * screen saying why. A record missing a street number now opens an empty bar that asks for
     * the real address. The parts themselves are still restored below.
     *
     * The prefill applies the same rule when it writes this key — the two writers of it have to
     * agree, or whichever ran last decides.
     */
    const businessAddress = isCompleteAddress(businessAddressParts)
      ? getFullAddress(businessAddressParts)
      : '';
    const businessInfo: BusinessInfoForm = {
      businessName: localUserRecord?.business_name || '',
      // Stored E.164, shown the way the form writes phones — the steps re-parse on submit.
      businessPhone: formatUSPhoneForDisplay(
        localUserRecord?.phone_number || ''
      ),
      address_st_name: localUserRecord?.address_st_name || '',
      address_st_number: localUserRecord?.address_st_number || '',
      address_unit: localUserRecord?.address_unit || '',
      address_city: localUserRecord?.address_city || '',
      address_state: localUserRecord?.address_state || '',
      address_zip: localUserRecord?.address_zip || '',
      taxId: localUserRecord?.employer_id_number || '',
      annualRevenue: application?.average_annual_revenue || 0,
      yearsInBusiness: application?.years_in_business
        ? String(application.years_in_business)
        : '',
      referralCode: application?.referral_code_used || '',
      state_of_registration: localUserRecord?.state_of_registration || '',
      businessAddress,
      partnerCompany: '',
    };

    safeSessionSetItem('businessInfo', JSON.stringify(businessInfo));

    const { driver } = localUserRecord || {};

    const dateOfBirth = formatDate(
      new Date(driver?.date_of_birth ?? new Date()),
      true
    );

    const accountAddressParts: AddressParts = {
      address_st_name: driver?.address_st_name || '',
      address_st_number: driver?.address_st_number || '',
      address_unit: driver?.address_unit || '',
      address_city: driver?.address_city || '',
      address_state: driver?.address_state || '',
      address_zip: driver?.address_zip || '',
    };
    const accountAddress = getFullAddress(accountAddressParts);

    const accountInfo: PersonalFormData = {
      firstName: driver?.first_name || '',
      lastName: driver?.last_name || '',
      email: driver?.email || '',
      phoneNumber: formatUSPhoneForDisplay(driver?.phone_number || ''),
      address_st_number: driver?.address_st_number || '',
      address_st_name: driver?.address_st_name || '',
      address_unit: driver?.address_unit || '',
      address_city: driver?.address_city || '',
      address_state: driver?.address_state || '',
      address_zip: driver?.address_zip || '',
      dateOfBirth,
      ssn: driver?.ssn || '',
      businessOwnershipPercentage: driver?.business_ownership_percentage || 0,
      driversLicenseNumber: driver?.drivers_license_number || '',
      driversLicenseState: driver?.drivers_license_state || '',
      address: accountAddress,
      otherBeneficialOwners: false,
    };
    safeSessionSetItem('personalInfo', JSON.stringify(accountInfo));
    setBusinessInfo(businessInfo);
    setPersonalInfo(accountInfo);
  }, [application, userRecord]);

  const checkSessionStorage = useCallback(() => {
    const savedDataPersonal = safeSessionGetItem('personalInfo');
    if (savedDataPersonal) {
      const parsedData: PersonalFormData = JSON.parse(savedDataPersonal);
      // No account email is no evidence of a different user — it is an account we cannot
      // identify by email at all, and the comparison below would then read ANY stored email as
      // somebody else's. That matters since THT-711: with no email of our own on file the
      // prefill may fill this field from the carrier registry, and treating that as proof of a
      // shared tab would wipe the customer's whole draft mid-signup.
      if (!userRecord?.email) return;
      if (userRecord.email === parsedData.email) return;
      safeSessionRemoveItem('personalInfo');
      safeSessionRemoveItem('businessInfo');
      safeSessionRemoveItem('documentUploadInfo');
      // The Highway prefill (THT-711) is written per confirmed carrier and has to be swept with
      // the rest of it — a stale asset count or "we found this from your MC/DOT" hint belonging
      // to whoever used this tab last is exactly the kind of thing this purge exists to stop.
      safeSessionRemoveItem(ASSET_PREFILL_STORAGE_KEY);
      safeSessionRemoveItem(PREFILL_SUGGESTIONS_STORAGE_KEY);
      // Legacy agreement drafts. Nothing writes these now — the merged step persists nothing —
      // but 'masterServiceAgreement' held a typed legal name and was never on any sweep list, so
      // a tab that still has one must not carry it into the next person's signature field.
      safeSessionRemoveItem('masterServiceAgreement');
      safeSessionRemoveItem('bankAgreement');
    }
  }, [userRecord]);

  useEffect(() => {
    const savedDataPersonal = safeSessionGetItem('personalInfo');
    const savedDataBusiness = safeSessionGetItem('businessInfo');
    const accountInfoData: PersonalFormData = savedDataPersonal
      ? JSON.parse(savedDataPersonal)
      : ({} as PersonalFormData);
    const businessInfoData: BusinessInfoForm = savedDataBusiness
      ? JSON.parse(savedDataBusiness)
      : ({} as BusinessInfoForm);

    const shouldSetBusinessInfo =
      userRecord?.status === 'pending' &&
      !businessInfoData?.businessName?.length &&
      !accountInfoData?.firstName?.length &&
      !!(userRecord as any)?.business_name?.length &&
      (application?.average_annual_revenue ?? 0) > 0;

    if (!shouldSetBusinessInfo) return;
    populateApplicationData();
  }, [
    application?.average_annual_revenue,
    populateApplicationData,
    userRecord,
  ]);

  useEffect(() => {
    if (
      isAuthenticated &&
      userRecord &&
      userRecord.userType === Entity.Customer &&
      (userRecord as any)?.customer_id?.length
    ) {
      checkSessionStorage();
    }
  }, [
    application?.application_id?.length,
    checkSessionStorage,
    isAuthenticated,
    userRecord,
  ]);

  useEffect(() => {
    const role = safeGetItem('tachUserRole') ?? '';
    if (role) {
      setSelectedRole(role as Entity);
    }
    if (
      userRecord &&
      userRecord?.userType === Entity.Customer &&
      userRecord?.customer_id?.length > 0 &&
      userRecord?.status === 'pending'
    ) {
      const fetchRecentApplicationByCustomerId = async (
        customer_id: string
      ) => {
        // Prevent duplicate fetches
        if (applicationFetchInProgress.current) {
          return;
        }

        applicationFetchInProgress.current = true;
        setLoading(true);
        try {
          const response = await callProtectedApi(
            `/applications/recent/${customer_id}`,
            'get',
            {}
          );

          const data: Application = response?.data?.data?.application;

          const appFound = response?.data?.data?.applicationFound;
          if (
            data &&
            Object.entries(data)?.length > 0 &&
            response?.status === HttpStatusCode.Ok &&
            appFound
          ) {
            setApplication(data);
            setStep(data?.application_step ?? 'personal_info');
          }
        } catch (e: any) {
          console.error(
            'Error occurred while fetching most recent customer application: ',
            e?.message
          );
        } finally {
          setLoading(false);
          applicationFetchInProgress.current = false;
        }
      };
      if (
        isAuthenticated &&
        userRecord &&
        userRecord?.customer_id?.length > 0
      ) {
        fetchRecentApplicationByCustomerId(userRecord?.customer_id);
      }
    } else if (userRecord && userRecord?.status !== 'pending') {
      // For non-pending users (active, terminated, etc.), don't load application
      setLoading(false);
    }
    // Active user routing is handled by AuthRouter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRecord, isAuthenticated, code]);

  useEffect(() => {
    if (
      userRecord &&
      userRecord?.userType === Entity.Customer &&
      userRecord?.customer_id?.length > 0
    ) {
      setSelectedRole(Entity.Customer);
    } else if (
      userRecord &&
      userRecord?.userType === Entity.Vendor &&
      userRecord?.vendor_id?.length > 0
    ) {
      setSelectedRole(Entity.Vendor);
      if (
        userRecord?.vendor_signup_step &&
        userRecord?.vendor_signup_step?.length > 0
      ) {
        setCurrStep(userRecord?.vendor_signup_step as VendorSignupStep);
      }
    } else if (
      userRecord &&
      userRecord?.userType === Entity.Partner &&
      userRecord?.partner_id?.length > 0
    ) {
      setSelectedRole(Entity.Partner);
      if (
        userRecord?.partner_signup_step &&
        userRecord?.partner_signup_step?.length > 0
      ) {
        setCurrStep(userRecord?.partner_signup_step as PartnerSignupStep);
      }
    }
  }, [userRecord]);

  // See DEPRECATED_STEP_REMAP: an application parked on a removed step resolves to the live
  // screen that replaced it, without the step being written back.
  const setStep = (newStep: string) => {
    setCurrStep(DEPRECATED_STEP_REMAP[newStep] ?? newStep);
  };

  const handleSetApplication = (app: Application) => {
    setApplication(app);
  };

  const renderSignupStep = (role: Entity, step: string) => {
    switch (currStep) {
      case ApplicationStep.PersonalInfo:
        return (
          <AccountInfo
            selectedRole={selectedRole}
            currStep={currStep}
            setStep={setStep}
            application={application}
          />
        );
      case ApplicationStep.BusinessInfo:
        return (
          <BusinessInfo
            selectedRole={selectedRole}
            currStep={currStep}
            setStep={setStep}
            setApplication={handleSetApplication}
            application={application}
            onEditCarrierIdentity={() => setEditingCarrierIdentity(true)}
          />
        );
      case ApplicationStep.ChoosePlan:
        return (
          <ChoosePlan
            selectedRole={selectedRole}
            setStep={setStep}
            currStep={currStep}
            application={application}
            setApplication={handleSetApplication}
          />
        );
      case ApplicationStep.Review:
        return (
          <Review
            setStep={setStep}
            currStep={currStep}
            selectedRole={selectedRole}
            application={application}
            initialPersonalInfo={personalInfo}
            initialBusinessInfo={businessInfo}
          />
        );
      case ApplicationStep.MasterServiceAgreement:
        return (
          <Agreements
            setStep={setStep}
            currStep={currStep}
            selectedRole={selectedRole}
            application={application}
          />
        );
      case ApplicationStep.Assets:
        return (
          <BusinessAssets
            setStep={setStep}
            currStep={currStep}
            selectedRole={selectedRole}
            application={application}
            setApplication={handleSetApplication}
          />
        );
    }
  };

  const renderSignupStepVendor = (role: Entity, step: string) => {
    switch (currStep) {
      case 'vendor_business_info':
        return (
          <VendorBusinessDetails
            selectedRole={selectedRole}
            currStep={currStep}
            setStep={setStep}
          />
        );
      case 'vendor_personal_info':
        return (
          <VendorPersonalInfo
            selectedRole={selectedRole}
            currStep={currStep}
            setStep={setStep}
          />
        );
      case 'vendor_management_contact':
        return (
          <VendorManagementContact
            setStep={setStep}
            currStep={currStep}
            selectedRole={selectedRole}
          />
        );
      case 'vendor_pending_review':
        return (
          <PendingReview
            setStep={setStep}
            currStep={currStep}
            selectedRole={selectedRole}
          />
        );
    }
  };

  const renderSignupStepPartner = (role: Entity, step: string) => {
    switch (currStep) {
      case 'partner_business_info':
        return (
          <PartnerBusinessDetails
            selectedRole={selectedRole}
            currStep={currStep}
            setStep={setStep}
          />
        );
      case 'partner_personal_info':
        return (
          <PartnerPersonalInfo
            selectedRole={selectedRole}
            currStep={currStep}
            setStep={setStep}
          />
        );
      case 'partner_management_contact':
        return (
          <PartnerManagementContact
            setStep={setStep}
            currStep={currStep}
            selectedRole={selectedRole}
          />
        );
      case 'partner_contract':
        return (
          <PartnerContract
            setStep={setStep}
            currStep={currStep}
            selectedRole={selectedRole}
          />
        );
      case 'partner_pending_review':
        return (
          <PendingReview
            setStep={setStep}
            currStep={currStep}
            selectedRole={selectedRole}
          />
        );
    }
  };

  const renderSignupFlow = (role: Entity) => {
    switch (role) {
      case Entity.Customer:
        return (
          <div
            className={`flex w-full flex-col ${screenView === 'desktop' ? 'h-full' : 'h-full overflow-hidden'}`}
          >
            {isAuthenticated &&
              (showHeadsUp ? (
                <HeadsUp
                  // A correction opens straight on the authority question: this customer has
                  // already read the intro on their way in, and came back for the one field.
                  correctingSavedIdentity={editingCarrierIdentity}
                  onContinue={async (carrierIdentitySaved) => {
                    // The heads-up page persists the carrier identity without touching the
                    // cached record, and Business Info reads that record to decide whether to
                    // ask again. Pull the fresh copy before handing over, or the customer is
                    // asked the same question twice in one session. Awaited on purpose: on the
                    // save path the heads-up page holds its own spinner until this resolves, so
                    // nothing flashes, and refetchUser never rejects.
                    //
                    // Skipped when nothing was saved. The save-failure skip link has no pending
                    // state, so awaiting a round-trip that cannot have changed anything would
                    // just make that link look broken.
                    if (carrierIdentitySaved) await refetchUser();
                    setHeadsUpCompleted(true);
                    // Whether they saved a new number or backed out, the correction is over —
                    // leaving this set would pin the customer on this page for the rest of the
                    // session, since the identity they now hold makes every other gate false.
                    setEditingCarrierIdentity(false);
                    setStep(ApplicationStep.BusinessInfo);
                  }}
                />
              ) : (
                renderSignupStep(role, currStep)
              ))}
          </div>
        );
      case Entity.Vendor:
        return (
          <div className="flex h-full w-full flex-col overflow-hidden">
            {isAuthenticated && renderSignupStepVendor(role, currStep)}
          </div>
        );
      case Entity.Partner:
        return (
          <div className="flex h-full w-full flex-col overflow-hidden">
            {isAuthenticated && renderSignupStepPartner(role, currStep)}
          </div>
        );
      case Entity.Driver:
      case Entity.PartnerAgent:
      default:
        // Routing handled by AuthRouter
        return null;
      // case 'repair-shop':
      // case 'part-shop':
      //   return (
      //     <>
      //       {currStep === 2 && isAuthenticated && (
      //         <VendorPersonalInfo
      //           isSmall={isSmallScreen}
      //           selectedRole={selectedRole}
      //           incrementStep={incrementStep}
      //           decrementStep={decrementStep}
      //           currStep={currStep}
      //         />
      //       )}
      //       {currStep === 3 && isAuthenticated && (
      //         <VendorBusinessDetails
      //           isSmall={isSmallScreen}
      //           selectedRole={selectedRole}
      //           incrementStep={incrementStep}
      //           decrementStep={decrementStep}
      //           currStep={currStep}
      //         />
      //       )}
      //       {currStep === 4 && isAuthenticated && (
      //         <Promotion
      //           isSmall={isSmallScreen}
      //           selectedRole={selectedRole}
      //           incrementStep={incrementStep}
      //           decrementStep={decrementStep}
      //           currStep={currStep}
      //         />
      //       )}
      //       {currStep === 5 && isAuthenticated && (
      //         <VendorManagementContacts
      //           isSmall={isSmallScreen}
      //           setStep={setStep}
      //           selectedRole={selectedRole}
      //           incrementStep={incrementStep}
      //           decrementStep={decrementStep}
      //           currStep={currStep}
      //         />
      //       )}
      //       {currStep === 7 && isAuthenticated && (
      //         <Review
      //           selectedRole={selectedRole}
      //           incrementStep={incrementStep}
      //           decrementStep={decrementStep}
      //           currStep={currStep}
      //           setStep={setStep}
      //           isSmall={isSmallScreen}
      //         />
      //       )}
      //     </>
      //   );
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div>
            {isLoading ? (
              <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
                <img
                  alt="loading-icon"
                  src="/Icon-v2.gif"
                  className="flex h-44 w-44 justify-center"
                />
                <h2 className="text-[14px] text-secondary">
                  Fetching your account record...
                </h2>
              </div>
            ) : (
              <>
                {isAuthenticated &&
                  userRecord &&
                  userId &&
                  userId?.length > 0 &&
                  (userRecord?.status === 'pending' ||
                    userRecord?.userType === Entity.Vendor ||
                    userRecord?.userType === Entity.Partner) && (
                    <>
                      {!loading ? (
                        <div
                          className={`flex h-screen w-full flex-col overflow-x-hidden`}
                        >
                          {currStep !==
                            ApplicationStep.ManualDocumentUpload && (
                            <SignupBreadcrumbs
                              selectedRole={selectedRole}
                              currStep={
                                showHeadsUp ? HEADS_UP_TOPBAR_STEP : currStep
                              }
                            />
                          )}
                          <div className="h-full flex-1 overflow-y-auto">
                            {renderSignupFlow(selectedRole)}
                          </div>
                        </div>
                      ) : (
                        <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
                          <img
                            alt="loading-icon"
                            src="/Icon-v2.gif"
                            className="flex h-44 w-44 justify-center"
                          />
                          <h2 className="text-[14px] text-secondary">
                            Checking for a current application...
                          </h2>
                        </div>
                      )}
                    </>
                  )}
              </>
            )}
          </div>
        );
      case 'tablet':
        return (
          <div>
            {isLoading ? (
              <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
                <img
                  alt="loading-icon"
                  src="/Icon-v2.gif"
                  className="flex h-44 w-44 justify-center"
                />
                <h2 className="text-[14px] text-secondary">
                  Fetching your account record...
                </h2>
              </div>
            ) : (
              <>
                {isAuthenticated &&
                  userRecord &&
                  userId &&
                  userId?.length > 0 &&
                  (userRecord?.status === 'pending' ||
                    userRecord?.userType === Entity.Vendor ||
                    userRecord?.userType === Entity.Partner) && (
                    <>
                      {!loading ? (
                        <div
                          className={`flex h-screen w-full flex-col justify-center gap-y-[16px] overflow-x-hidden`}
                        >
                          {currStep !==
                            ApplicationStep.ManualDocumentUpload && (
                            <SignupBreadcrumbs
                              selectedRole={selectedRole}
                              currStep={
                                showHeadsUp ? HEADS_UP_TOPBAR_STEP : currStep
                              }
                            />
                          )}
                          {renderSignupFlow(selectedRole)}
                        </div>
                      ) : (
                        <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
                          <img
                            alt="loading-icon"
                            src="/Icon-v2.gif"
                            className="flex h-44 w-44 justify-center"
                          />
                          <h2 className="text-[14px] text-secondary">
                            Checking for a current application...
                          </h2>
                        </div>
                      )}
                    </>
                  )}
              </>
            )}
          </div>
        );
      case 'mobile':
        return (
          <div>
            {isLoading ? (
              <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
                <img
                  alt="loading-icon"
                  src="/Icon-v2.gif"
                  className="flex h-44 w-44 justify-center"
                />
                <h2 className="text-[14px] text-secondary">
                  Fetching your account record...
                </h2>
              </div>
            ) : (
              <>
                {isAuthenticated &&
                  userRecord &&
                  userId &&
                  userId?.length > 0 &&
                  userRecord?.status === 'pending' && (
                    <>
                      {!loading ? (
                        <div
                          className={`flex h-screen w-full flex-col justify-center gap-y-[16px] overflow-x-hidden`}
                        >
                          {currStep !==
                            ApplicationStep.ManualDocumentUpload && (
                            <SignupBreadcrumbs
                              selectedRole={selectedRole}
                              currStep={
                                showHeadsUp ? HEADS_UP_TOPBAR_STEP : currStep
                              }
                            />
                          )}
                          {renderSignupFlow(selectedRole)}
                        </div>
                      ) : (
                        <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
                          <img
                            alt="loading-icon"
                            src="/Icon-v2.gif"
                            className="flex h-44 w-44 justify-center"
                          />
                          <h2 className="text-[14px] text-secondary">
                            Checking for a current application...
                          </h2>
                        </div>
                      )}
                    </>
                  )}
              </>
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default Signup;

export const patchApplication = async ({
  updates,
  application_id,
  customer_id,
  callProtectedApi,
  setLoading,
}: {
  updates: PatchApplicationRequest;
  application_id: string;
  customer_id: string;
  callProtectedApi: (
    url: string,
    requestType: string,
    body: any,
    errorMsg: string,
    newHeaders?: any,
    domain?: string | undefined,
    retries?: number
  ) => Promise<AxiosResponse<any, any> | null>;
  setLoading: (loading: boolean) => void;
}) => {
  setLoading(true);
  try {
    const response = await callProtectedApi(
      `/applications/upsert`,
      'post',
      { ...updates, application_id, customer_id },
      'Error occurred while updating your application.'
    );
    const data = response?.data?.data?.application;
    if (
      data &&
      Object.entries(data)?.length > 0 &&
      (response?.status === HttpStatusCode.Ok ||
        response?.status === HttpStatusCode.Created)
    ) {
      return data?.application_step;
    }
  } catch (e: any) {
    console.error('Error occurred while updating application: ', e?.message);
  }
};

const returnApplicationStatus = (
  previousStep: string,
  applicationStatus: ApplicationStatus
) => {
  switch (previousStep) {
    case ApplicationStep.PlaidLinkRetry:
      return ApplicationStatus.COMPLETE_PLAID_LINK_RETRY;
    case ApplicationStep.ManualDocumentUpload:
      if (applicationStatus === ApplicationStatus.REJECTED_BLUE_PENGUIN) {
        return ApplicationStatus.COMPLETE_BLUE_PENGUIN_DOCUMENTS;
      }
      return ApplicationStatus.COMPLETE_DOCUMENT_REQUEST;
    default:
      return undefined;
  }
};

export const setApplicationStep = async ({
  step,
  application_id,
  customer_id,
  callProtectedApi,
  setLoading,
  previousStep,
  applicationStatus,
  additionalFields,
  onInvalidIdentifier,
  onApplicationUpdated,
}: {
  step: string;
  application_id: string;
  customer_id: string;
  callProtectedApi: (
    url: string,
    requestType: string,
    body: any,
    errorMsg: string,
    newHeaders?: any,
    domain?: string | undefined,
    retries?: number,
    hideAlert?: boolean
  ) => Promise<AxiosResponse<any, any> | null>;
  setLoading: (loading: boolean) => void;
  previousStep?: string;
  applicationStatus?: ApplicationStatus;
  additionalFields?: Record<string, any>;
  // Invoked when the backend rejects the MC/DOT number (Highway lookup miss on the review
  // transition) so the caller can show a specific message / bounce back to Business Info.
  onInvalidIdentifier?: () => void;
  // Invoked with the application the upsert echoes back. The page fetches the application once,
  // when the user record lands, and never again between steps — so a caller that writes a field
  // it (or a later step) reads back has to hand the fresh record up, or the page keeps rendering
  // the pre-write value. Choose Tier's selected_tier is exactly that case.
  onApplicationUpdated?: (application: Application) => void;
}) => {
  setLoading(true);
  try {
    const response = await callProtectedApi(
      '/applications/upsert',
      'post',
      {
        application_step: step,
        status: returnApplicationStatus(
          previousStep ?? '',
          applicationStatus ?? ('' as ApplicationStatus)
        ),
        application_id: application_id,
        customer_id,
        ...additionalFields,
      },
      'Error occurred while updating application step.',
      undefined,
      undefined,
      0,
      // Suppress the generic error toast when the caller handles the invalid-carrier case
      // itself (it shows a specific message and bounces to Business Info).
      !!onInvalidIdentifier
    );
    const data = response?.data?.data?.application;
    if (
      data &&
      Object.entries(data)?.length > 0 &&
      (response?.status === HttpStatusCode.Ok ||
        response?.status === HttpStatusCode.Created)
    ) {
      onApplicationUpdated?.(data);
      return data?.application_step;
    } else if (
      response?.status === HttpStatusCode.BadRequest &&
      response?.data?.data?.invalidIdentifier
    ) {
      // Highway couldn't match the MC/DOT number entered on Business Info.
      onInvalidIdentifier?.();
    }
  } catch (e: any) {
    console.error(
      'Error occurred while updating application step: ',
      e?.message
    );
  }
};
